import type { MutableRefObject } from 'react';

import type { NovaNavigation, NovaNavigationActions, NovaView } from './types';
import type { SettingsPanelCloseGuard } from '../components/settings-panel-context';
import { normalizeChatUuid } from '../lib/notification-helpers';

export function sameNovaView(left: NovaView, right: NovaView): boolean {
  if (left.kind !== right.kind) return false;
  if (left.kind === 'chat' && right.kind === 'chat') {
    return left.chatUuid === right.chatUuid && left.messageUuid === right.messageUuid;
  }
  if (left.kind === 'scheduled' && right.kind === 'scheduled') {
    return left.scheduleUuid === right.scheduleUuid && !!left.isCreating === !!right.isCreating;
  }
  if (left.kind === 'settings' && right.kind === 'settings') {
    return (
      left.section === right.section &&
      left.tab === right.tab &&
      left.organizationId === right.organizationId &&
      left.teamId === right.teamId &&
      left.subView?.type === right.subView?.type &&
      (left.subView?.type !== 'custom-skill-edit' ||
        (right.subView?.type === 'custom-skill-edit' && left.subView.skillUuid === right.subView.skillUuid))
    );
  }
  return false;
}

interface NavigationState {
  navigation: NovaNavigation;
  returnToChat: Extract<NovaView, { kind: 'chat' }>;
  chatUuid: string;
  isReadOnly: boolean;
}
interface NavigationEffects {
  selectChat: (chatUuid: string, accountUuid?: string | null) => void;
  startNewChat: NovaNavigationActions['startNewChat'];
  showChat: () => void;
}

/** All user gestures converge here. A guard is consulted before committing any state or navigation. */
export function createNovaNavigationActions(
  getState: () => NavigationState,
  backHandlerRef: MutableRefObject<SettingsPanelCloseGuard | null>,
  effects: NavigationEffects,
): NovaNavigationActions {
  let guardPending = false;
  const guarded = (commit: () => void) => {
    if (guardPending) return;
    const guard = backHandlerRef.current;
    if (!guard) {
      commit();
      return;
    }
    const originalView = getState().navigation.view;
    const finish = (allowed: boolean | void) => {
      guardPending = false;
      // Ignore a confirmation for a screen that has since changed externally.
      if (allowed === false || !sameNovaView(originalView, getState().navigation.view)) return;
      backHandlerRef.current = null;
      commit();
    };
    const result = guard();
    if (result instanceof Promise) {
      guardPending = true;
      void result.then(finish, () => {
        guardPending = false;
      });
    } else finish(result);
  };
  const navigate = (view: NovaView, history: 'push' | 'replace' = 'push') => {
    const current = getState().navigation;
    if (!sameNovaView(current.view, view)) current.navigate(view, { history });
  };
  const parent = (): NovaView => {
    const { view } = getState().navigation;
    if (view.kind === 'settings' && view.subView) return { ...view, subView: undefined };
    if (view.kind === 'scheduled' && (view.scheduleUuid || view.isCreating)) return { kind: 'scheduled' };
    return getState().returnToChat;
  };
  const selectChat = (
    chatUuid: string,
    accountUuid?: string | null,
    messageUuid?: string,
    history: 'push' | 'replace' = 'push',
  ) => {
    effects.selectChat(chatUuid, accountUuid);
    navigate({ kind: 'chat', chatUuid: normalizeChatUuid(chatUuid), ...(messageUuid ? { messageUuid } : {}) }, history);
  };
  const actions: NovaNavigationActions = {
    openChat: (uuid, account) => guarded(() => selectChat(uuid, account)),
    startNewChat: (input, options) => {
      if (getState().isReadOnly) return;
      guarded(() => {
        effects.startNewChat(input, options);
        navigate({ kind: 'chat' });
      });
    },
    openSettings: (section = 'general') => guarded(() => navigate({ kind: 'settings', section })),
    toggleSettings: () =>
      guarded(() => {
        const { view } = getState().navigation;
        navigate(view.kind === 'settings' ? parent() : { kind: 'settings', section: 'general' });
      }),
    closeSettings: () => guarded(() => navigate(getState().returnToChat)),
    openScheduled: () => guarded(() => navigate({ kind: 'scheduled' })),
    toggleScheduled: () =>
      guarded(() => navigate(getState().navigation.view.kind === 'scheduled' ? parent() : { kind: 'scheduled' })),
    openSubView: (subView) =>
      guarded(() => {
        const { view } = getState().navigation;
        if (subView.type === 'nova-schedule-detail')
          navigate({ kind: 'scheduled', scheduleUuid: subView.scheduleUuid });
        else {
          const section =
            subView.type === 'outlook-manage' ? 'general' : subView.type === 'team-create' ? 'teams' : 'skills';
          navigate({
            ...(view.kind === 'settings' && view.section === section ? view : { kind: 'settings', section }),
            subView,
          });
        }
      }),
    closeSubView: () => guarded(() => navigate(parent())),
    createSchedule: () => guarded(() => navigate({ kind: 'scheduled', isCreating: true })),
    scheduleCreated: (scheduleUuid) => {
      const { view } = getState().navigation;
      // The mutation can finish after the user has left the create form.
      if (view.kind !== 'scheduled' || !view.isCreating) return;
      guarded(() => navigate({ kind: 'scheduled', scheduleUuid }, 'replace'));
    },
    openChatAtMessage: (uuid, message) => guarded(() => selectChat(uuid, undefined, message)),
    clearMessageTarget: (target) => {
      const { view } = getState().navigation;
      if (
        view.kind === 'chat' &&
        view.chatUuid === normalizeChatUuid(target.chatUuid) &&
        view.messageUuid === target.messageUuid
      ) {
        navigate({ kind: 'chat', chatUuid: view.chatUuid }, 'replace');
      }
    },
    chatCreated: (uuid) => {
      const { navigation, chatUuid } = getState();
      // A late send must not take the user away from another screen or chat.
      if (
        navigation.view.kind === 'chat' &&
        !navigation.view.chatUuid &&
        normalizeChatUuid(chatUuid) === normalizeChatUuid(uuid)
      ) {
        navigate({ kind: 'chat', chatUuid: normalizeChatUuid(uuid) }, 'replace');
      }
    },
    setSkillsTab: (tab) =>
      guarded(() => {
        const { view } = getState().navigation;
        if (view.kind === 'settings' && view.section === 'skills') navigate({ ...view, tab }, 'replace');
      }),
    setAnalyticsScope: (scope) =>
      guarded(() => {
        const { view } = getState().navigation;
        if (view.kind === 'settings' && view.section === 'analytics')
          navigate({ ...view, organizationId: scope?.organizationId, teamId: scope?.teamId ?? undefined }, 'replace');
      }),
    openNotification: (uuid) =>
      guarded(() => {
        effects.showChat();
        selectChat(uuid);
      }),
    archiveCurrentChat: (uuid) =>
      guarded(() => {
        if (uuid) selectChat(uuid, undefined, undefined, 'replace');
        else {
          effects.startNewChat();
          navigate({ kind: 'chat' }, 'replace');
        }
      }),
    followInitialChat: (uuid) => {
      const { navigation, chatUuid } = getState();
      if (
        normalizeChatUuid(uuid) === normalizeChatUuid(chatUuid) ||
        (navigation.view.kind === 'chat' && navigation.view.chatUuid === normalizeChatUuid(uuid))
      )
        return;
      guarded(() => selectChat(uuid, undefined, undefined, 'replace'));
    },
    canonicalize: (view) => navigate(view, 'replace'),
    requestClose: (afterClose) =>
      guarded(() => {
        if (afterClose) afterClose();
        else navigate(getState().returnToChat);
      }),
  };
  return actions;
}
